import React, { useState, useEffect, useRef } from 'react';
import {
    Paper, Box, Typography, IconButton, TextField, Button, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { useChat } from './ChatContext';
import { useSelector } from 'react-redux';

const ChatWindow: React.FC = () => {
    const { currentChat, setCurrentChat, sendMessage, deleteChat } = useChat();
    const user = useSelector((state: any) => state.user.user);
    const [newMessage, setNewMessage] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [currentChat?.messages]);

    if (!currentChat || !currentChat.senderId || !currentChat.receiverId) return null;

    const otherUser = currentChat.senderId._id === user._id ? currentChat.receiverId : currentChat.senderId;

    // Debug log
    console.log('ChatWindow - currentChat:', currentChat);
    console.log('ChatWindow - otherUser:', otherUser);

    const handleSend = () => {
        if (newMessage.trim()) {
            sendMessage(newMessage);
            setNewMessage('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleConfirmDelete = async () => {
        if (currentChat) {
            await deleteChat(currentChat._id);
            setConfirmDeleteOpen(false);
            handleCloseMenu();
        }
    };

    return (
        <Paper
            elevation={3}
            sx={{
                position: 'fixed',
                bottom: 80,
                right: 20,
                width: 300,
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1000,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <IconButton size="small" onClick={() => setCurrentChat(null)} sx={{ color: 'white', mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Avatar src={otherUser?.image || ''} alt={otherUser?.name || 'User'} sx={{ width: 32, height: 32, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {otherUser?.name || 'Usuário'}
                </Typography>
                <IconButton size="small" onClick={handleDeleteClick} sx={{ color: 'white' }}>
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                >
                    <MenuItem onClick={() => setConfirmDeleteOpen(true)}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Excluir Conversa
                    </MenuItem>
                </Menu>
            </Box>

            {/* Messages */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {currentChat.messages.map((msg, index) => {
                    const isMe = msg.senderId === user._id;
                    const messageDate = new Date(msg.createdAt);
                    const formattedTime = messageDate.toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });

                    return (
                        <Box
                            key={index}
                            sx={{
                                alignSelf: isMe ? 'flex-end' : 'flex-start',
                                maxWidth: '80%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: isMe ? 'primary.light' : 'grey.200',
                                    color: isMe ? 'white' : 'text.primary',
                                    p: 1,
                                    borderRadius: 2,
                                    fontSize: '0.875rem'
                                }}
                            >
                                {msg.text}
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    color: 'text.secondary',
                                    alignSelf: isMe ? 'flex-end' : 'flex-start',
                                    px: 0.5
                                }}
                            >
                                {formattedTime}
                            </Typography>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 1, display: 'flex', gap: 1, borderTop: 1, borderColor: 'divider' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    multiline
                    maxRows={2}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!newMessage.trim()}>
                    <SendIcon />
                </IconButton>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                <DialogTitle>Excluir Conversa?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir esta conversa permanentemente? Esta ação não pode ser desfeita.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ChatWindow;
